import { captureAcquisition } from "./shared/helpers/acquisition.helper";
import { PrimeNGConfig } from "primeng/api";
import { fromEvent, map, merge, of, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

import { Component, DestroyRef, inject, OnDestroy, OnInit } from "@angular/core";
import { NavigationError, NavigationStart, Router } from "@angular/router";
import { handleChunkLoadError, isChunkLoadError } from "./helpers/chunk-error-handler";

import { EvaService } from "./core/services/eva.service";
import { AuthenticationService } from "./services/authentication.service";
import { applyEvaZIndexLayers } from "./shared/ds/eva-dialog/eva-z-layers";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  networkStatus: boolean = true;
  networkStatus$: Subscription = Subscription.EMPTY;

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private config: PrimeNGConfig,
    public evaService: EvaService,
  ) {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => captureAcquisition("", `#${event.url}`));

    this.router.events
      .pipe(
        filter((event): event is NavigationError => event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        if (isChunkLoadError(event.error)) {
          void handleChunkLoadError();
        }
      });
  }

  ngOnInit(): void {
    this.checkNetworkStatus();
    applyEvaZIndexLayers(this.config);
    this.config.setTranslation({
      // Calendrier
      dateFormat: "dd.mm.yy",
      firstDayOfWeek: 1,
      dayNames: ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"],
      dayNamesShort: ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"],
      dayNamesMin: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
      monthNames: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ],
      monthNamesShort: ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"],
      today: "Aujourd'hui",
      clear: "Effacer",
      weekHeader: "Sem",

      // Password strength
      weak: "Faible",
      medium: "Moyen",
      strong: "Fort",
      passwordPrompt: "Saisir un mot de passe",

      // Messages génériques
      accept: "Oui",
      reject: "Non",
      choose: "Choisir",
      cancel: "Annuler",
      apply: "Appliquer",

      // Upload
      upload: "Téléverser",
      pending: "En attente",
      fileSizeTypes: ["o", "Ko", "Mo", "Go", "To", "Po", "Eo", "Zo", "Yo"],

      // Table/Pagination
      startsWith: "Commence par",
      contains: "Contient",
      notContains: "Ne contient pas",
      endsWith: "Se termine par",
      equals: "Égal à",
      notEquals: "Différent de",
      noFilter: "Aucun filtre",
      lt: "Inférieur à",
      lte: "Inférieur ou égal à",
      gt: "Supérieur à",
      gte: "Supérieur ou égal à",
      is: "Est",
      isNot: "N'est pas",
      before: "Avant",
      after: "Après",
      dateIs: "Date égale à",
      dateIsNot: "Date différente de",
      dateBefore: "Date avant",
      dateAfter: "Date après",
      matchAll: "Correspond à tous",
      matchAny: "Correspond à un",
      addRule: "Ajouter une règle",
      removeRule: "Supprimer la règle",

      // Divers
      emptyMessage: "Aucun résultat trouvé",
      emptyFilterMessage: "Aucun résultat trouvé",
      emptySearchMessage: "Aucun résultat trouvé",
      selectionMessage: "{0} éléments sélectionnés",
      searchMessage: "{0} résultats disponibles",
    });
  }

  ngOnDestroy(): void {
    this.networkStatus$.unsubscribe();
  }

  // CHECK NETWORK STATUS
  checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(of(null), fromEvent(window, "online"), fromEvent(window, "offline"))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
      });
  }

  // FORCE LOGOUT
  logout() {
    this.authenticationService.logout();
    this.router.navigate(["/login"]);
  }
}
